import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { loadProducts } from '@sophocles/features/warehouse/products/api';
import { productSlice } from '@sophocles/features/warehouse/products/slice';
import { reloadDataIfNeeded } from '@sophocles/providers/helpers';

// Module-level guard so rapid remounts (e.g. parent re-renders that
// give SalesProvider a fresh React identity before `lastLoaded` is set)
// piggyback on the in-flight request instead of firing parallel
// /products/ GETs.
let inFlightProductsLoad = null;

function SalesProvider({ children }) {
  const dispatch = useDispatch();

  const productsLastLoaded = useSelector(productSlice.selectors.selectLastLoaded);
  // Keep `lastLoaded` accessible from inside `fetchData` without putting
  // it in the effect deps — the dep used to refire the effect on every
  // successful load, which made remount races more likely.
  const productsLastLoadedRef = useRef(productsLastLoaded);
  productsLastLoadedRef.current = productsLastLoaded;

  useEffect(() => {
    const fetchData = async () => {
      if (inFlightProductsLoad) {
        // Another caller is already loading — await it instead of
        // dispatching a parallel request.
        try {
          await inFlightProductsLoad;
        } catch {
          // Surfaced via the originating dispatch's rejected action.
        }
        return;
      }

      dispatch(productSlice.actions.setIsLoading(true));
      inFlightProductsLoad = (async () => {
        try {
          await reloadDataIfNeeded(dispatch, loadProducts, { limit: 1000 }, productsLastLoadedRef.current);
        } finally {
          dispatch(productSlice.actions.setIsLoading(false));
          inFlightProductsLoad = null;
        }
      })();
      await inFlightProductsLoad;
    };

    fetchData();

    // Periodic reload — once per 5 minutes per mounted SalesProvider.
    const interval = setInterval(fetchData, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [dispatch]);

  return children;
}

export default SalesProvider;
